import { wsService } from "./wsService";

/**
 * Conteo ciclico de stock y alta de stock inicial.
 *
 * La seccion es "conteo" y no "stock-count" a proposito: tiene que coincidir con el module.key
 * del backend, porque User::authorizeRoles() saca el permiso del primer segmento de la URL.
 * Las tablas y las clases del back estan en ingles; de la ruta para afuera todo habla espaniol.
 */
const section = "conteo";

//--- Estados de la cabecera (espejo de App\Constants\StockCountStatus)
const STATUS_BORRADOR = "BOR";
const STATUS_EN_PROCESO = "PRO";
const STATUS_EN_REVISION = "REV";
const STATUS_CERRADO = "CER";
const STATUS_CANCELADO = "CAN";

//--- Tipos (espejo de App\Constants\StockCountType)
const TYPE_CICLICO = "CIC";
const TYPE_ALTA_INICIAL = "INI";

//--- Estados de la linea por ubicacion (espejo de App\Constants\StockCountLocationStatus)
const LOCATION_PENDIENTE = "PEN";
const LOCATION_CONTADA = "CON";
const LOCATION_CON_DIFERENCIA = "DIF";
const LOCATION_CERRADA = "CER";
const LOCATION_EXCLUIDA = "EXC";
//Segundo conteo pedido desde la revision, esperando al operario (plan-conteo-reconteo.md)
const LOCATION_RECONTEO = "REC";

//--- Estados de la linea de detalle (espejo de App\Constants\StockCountLineStatus)
const LINE_PENDIENTE = "PEN";
const LINE_COINCIDE = "OK";
const LINE_CON_DIFERENCIA = "DIF";
const LINE_AJUSTADA = "AJU";
const LINE_DESCARTADA = "DES";

//--- Resultado del segundo conteo por linea (espejo de App\Constants\StockCountRecountResult).
//No se persiste: el backend lo calcula del historico y lo manda en resultado_reconteo.
const RECOUNT_CONFIRMADA = "CONF";
const RECOUNT_CORREGIDA = "CORR";
const RECOUNT_INCONSISTENTE = "INC";
const RECOUNT_NUEVA = "NUE";

// -------------------------------------------------------------------
// CRUD y planificacion
// -------------------------------------------------------------------

function filter(param = {}) {
  return wsService.make_post_request(section + "/list", param);
}

function get(param = {}) {
  return wsService.make_post_request(section + "/get", param);
}

function create(param = {}) {
  return wsService.make_post_request(section + "/create", param);
}

function update(param = {}) {
  return wsService.make_put_request(section + "/update", param);
}

function remove(param = {}) {
  return wsService.make_delete_request(section + "/delete", param);
}

function list_states() {
  return wsService.make_post_request(section + "/list-states", {});
}

function list_types() {
  return wsService.make_post_request(section + "/list-types", {});
}

function list_location_states() {
  return wsService.make_post_request(section + "/list-location-states", {});
}

// -------------------------------------------------------------------
// Seleccion de ubicaciones (solo en BORRADOR)
// -------------------------------------------------------------------

/**
 * Ubicaciones candidatas, ya ordenadas por recorrido (zona -> rack -> columna -> nivel).
 *
 * NO pagina y NO tiene tope: devuelve todas las que matcheen. El orden del recorrido se resuelve
 * sobre el conjunto completo, asi que paginar daria un orden global equivocado — la pagina 2
 * podria empezar antes que la 1.
 */
function preview_ubicaciones(param = {}) {
  return wsService.make_post_request(section + "/preview-ubicaciones", param);
}

/**
 * Ubicaciones ya cargadas en un conteo. Se piden aparte del documento: son EXTRA_LAZY en el
 * back justamente para que el listado de conteos no arrastre cientos de filas por cada uno.
 *
 * El alta y la baja NO tienen endpoint propio: las ubicaciones viajan como locations_arr
 * dentro de create/update y las resuelve editCollection.
 *
 * Con include_contadores: true cada ubicacion trae pendientes / marcadas / con_diferencia, que
 * es lo que necesita el listado del Detalle sin pedir las lineas.
 */
function list_ubicaciones(param = {}) {
  return wsService.make_post_request(section + "/list-ubicaciones", param);
}

/**
 * Cuantas ubicaciones tiene con stock un cliente en un almacen. Solo numeros, sin la lista.
 *
 * Se llama al entrar al step 2 y al cambiar de cliente, sin que el usuario apriete nada: por eso
 * NO puede usar preview_ubicaciones, que no pagina ni tiene tope y se traeria todo a memoria.
 *
 * ubicaciones_ids son las que el front ya tiene cargadas: viajan porque al crear el conteo
 * todavia no existe en la base y esa lista vive solo en la pantalla.
 */
function contar_ubicaciones_cliente(param = {}) {
  return wsService.make_post_request(section + "/contar-ubicaciones-cliente", param);
}

// -------------------------------------------------------------------
// Transiciones
// -------------------------------------------------------------------

/** BORRADOR -> EN PROCESO. Congela el teorico, bloquea ubicaciones y emite las tareas. */
function emitir(param = {}) {
  return wsService.make_post_request(section + "/emitir", param);
}

function cancel(param = {}) {
  return wsService.make_post_request(section + "/cancel", param);
}

function cerrar(param = {}) {
  return wsService.make_post_request(section + "/cerrar", param);
}

// -------------------------------------------------------------------
// Revision y aprobacion
// -------------------------------------------------------------------

/**
 * Las lineas de una ubicacion ya armadas para mostrar, con sus contadores. Param: stock_count_location_id
 * Es el unico lugar de donde salen las lineas: el Detalle las pide al abrir una ubicacion.
 */
function list_lineas_ubicacion(param = {}) {
  return wsService.make_post_request(section + "/list-lineas-ubicacion", param);
}

/**
 * Solo MARCA la linea. El ajuste lo aplica aprobar_ubicacion, con el desbloqueo primero.
 * Param: stock_count_line_id, o stock_count_line_ids para la seleccion masiva.
 */
function aprobar_linea(param = {}) {
  return wsService.make_post_request(section + "/aprobar-linea", param);
}

/** Param: stock_count_line_id o stock_count_line_ids, y motivo (el mismo para todas). */
function descartar_linea(param = {}) {
  return wsService.make_post_request(section + "/descartar-linea", param);
}

/** Deshace un descarte. Solo mientras la ubicacion siga con diferencias sin aplicar. */
function revertir_descarte_linea(param = {}) {
  return wsService.make_post_request(section + "/revertir-descarte-linea", param);
}

/**
 * Desbloquea la ubicacion y recien ahi aplica los ajustes de las lineas marcadas.
 * Param: stock_count_location_id, o stock_count_location_ids para aplicar varias desde el listado.
 */
function aprobar_ubicacion(param = {}) {
  return wsService.make_post_request(section + "/aprobar-ubicacion", param);
}

/**
 * Pide el segundo conteo ciego de una o varias ubicaciones con diferencia.
 * Param: stock_count_location_ids (array). Todo o nada: si una no se puede
 * recontar, no se emite ninguna.
 */
function pedir_reconteo(param = {}) {
  return wsService.make_post_request(section + "/pedir-reconteo", param);
}

/**
 * Cancela el segundo conteo pedido de una ubicacion: vuelve a "con diferencia" con el primer
 * conteo vigente. Param: stock_count_location_id. Solo mientras la tarea no se cerró.
 */
function cancelar_reconteo(param = {}) {
  return wsService.make_post_request(section + "/cancelar-reconteo", param);
}

function reporte(param = {}) {
  return wsService.make_post_request(section + "/reporte", param);
}

function exportar(param = {}) {
  return wsService.make_post_request(section + "/export", param);
}

// -------------------------------------------------------------------
// Helpers de presentacion
// -------------------------------------------------------------------

function getStatusName(status_id) {
  switch (status_id) {
    case STATUS_BORRADOR:
      return "Borrador";
    case STATUS_EN_PROCESO:
      return "En proceso";
    case STATUS_EN_REVISION:
      return "En revision";
    case STATUS_CERRADO:
      return "Cerrado";
    case STATUS_CANCELADO:
      return "Cancelado";
    default:
      return "Desconocido";
  }
}

/**
 * Clase CSS del badge de estado. Se usa junto a status-badge, igual que el resto del sistema
 * (ver Asn.vue y ShipmentOrders.vue): el color vive en _dashboard.scss, no en JS.
 *
 * Tres familias con prefijo propio porque los codigos se pisan entre si y con los de recepcion:
 * 'PEN' y 'CER' existen en varias.
 */
function getStatusClass(status_id) {
  return "status-conteo-" + status_id;
}

function getLocationStatusName(status_id) {
  switch (status_id) {
    case LOCATION_PENDIENTE:
      return "Pendiente";
    case LOCATION_CONTADA:
      return "Contada";
    case LOCATION_CON_DIFERENCIA:
      return "Con diferencia";
    case LOCATION_CERRADA:
      return "Cerrada";
    case LOCATION_EXCLUIDA:
      return "Excluida";
    case LOCATION_RECONTEO:
      return "Segundo conteo";
    default:
      return "Desconocido";
  }
}

function getLocationStatusClass(status_id) {
  return "status-conteo-ubi-" + status_id;
}

function getLineStatusName(status_id) {
  switch (status_id) {
    case LINE_PENDIENTE:
      return "Pendiente";
    case LINE_COINCIDE:
      return "Sin diferencia";
    case LINE_CON_DIFERENCIA:
      return "Con diferencia";
    case LINE_AJUSTADA:
      return "Ajustada";
    case LINE_DESCARTADA:
      return "Diferencia descartada";
    default:
      return "Desconocido";
  }
}

function getLineStatusClass(status_id) {
  return "status-conteo-linea-" + status_id;
}

/** Color del chip del resultado del segundo conteo. */
function getRecountResultColor(result_id) {
  switch (result_id) {
    case RECOUNT_CONFIRMADA:
      return "red";
    case RECOUNT_CORREGIDA:
      return "green";
    case RECOUNT_INCONSISTENTE:
      return "deep-purple";
    case RECOUNT_NUEVA:
      return "orange";
    default:
      return "blue-grey";
  }
}

/** Lo que el chip le dice al revisor (R9 del plan): que hacer con la linea. */
function getRecountResultHint(result_id) {
  switch (result_id) {
    case RECOUNT_CONFIRMADA:
      return "Los dos conteos dieron lo mismo y distinto del teórico: la diferencia es real. Se sugiere ajustar.";
    case RECOUNT_CORREGIDA:
      return "El segundo conteo coincidió con el teórico: el primero estaba mal.";
    case RECOUNT_INCONSISTENTE:
      return "Ningún conteo coincide con el otro ni con el teórico. No hay tercer conteo: decide el revisor.";
    case RECOUNT_NUEVA:
      return "El primer conteo no tuvo diferencia y el segundo sí: los dos conteos no coinciden entre sí.";
    default:
      return "";
  }
}

/** El documento solo se edita y se borra en BORRADOR: una vez emitido se cancela. */
function isEditable(status_id) {
  return status_id === STATUS_BORRADOR;
}

/** Abierto = todavia puede cambiar. Cerrado y cancelado son finales. */
function isAbierto(status_id) {
  return [STATUS_BORRADOR, STATUS_EN_PROCESO, STATUS_EN_REVISION].includes(status_id);
}

/** Solo se puede cerrar a mano desde EN PROCESO o EN REVISION. */
function canCerrar(status_id) {
  return [STATUS_EN_PROCESO, STATUS_EN_REVISION].includes(status_id);
}

/** La linea espera una decision del revisor. */
function isPendienteDeResolucion(status_id) {
  return status_id === LINE_CON_DIFERENCIA;
}

/** La linea se descarto: es la unica decision que se puede deshacer. */
function isDescartada(status_id) {
  return status_id === LINE_DESCARTADA;
}

/** Tuvo diferencia alguna vez, la haya resuelto o no. Sin diferencia = OK o PEN. */
function tuvoDiferencia(status_id) {
  return [LINE_CON_DIFERENCIA, LINE_AJUSTADA, LINE_DESCARTADA].includes(status_id);
}

export const conteoService = {
  section,

  STATUS_BORRADOR,
  STATUS_EN_PROCESO,
  STATUS_EN_REVISION,
  STATUS_CERRADO,
  STATUS_CANCELADO,

  TYPE_CICLICO,
  TYPE_ALTA_INICIAL,

  LOCATION_PENDIENTE,
  LOCATION_CONTADA,
  LOCATION_CON_DIFERENCIA,
  LOCATION_CERRADA,
  LOCATION_EXCLUIDA,
  LOCATION_RECONTEO,

  LINE_PENDIENTE,
  LINE_COINCIDE,
  LINE_CON_DIFERENCIA,
  LINE_AJUSTADA,
  LINE_DESCARTADA,

  RECOUNT_CONFIRMADA,
  RECOUNT_CORREGIDA,
  RECOUNT_INCONSISTENTE,
  RECOUNT_NUEVA,

  filter,
  get,
  create,
  update,
  remove,
  list_states,
  list_types,
  list_location_states,

  preview_ubicaciones,
  list_ubicaciones,
  contar_ubicaciones_cliente,

  emitir,
  cancel,
  cerrar,

  list_lineas_ubicacion,
  aprobar_linea,
  descartar_linea,
  revertir_descarte_linea,
  aprobar_ubicacion,
  pedir_reconteo,
  cancelar_reconteo,
  reporte,
  exportar,

  getStatusName,
  getStatusClass,
  getLocationStatusName,
  getLocationStatusClass,
  getLineStatusName,
  getLineStatusClass,
  getRecountResultColor,
  getRecountResultHint,
  isEditable,
  isAbierto,
  canCerrar,
  isPendienteDeResolucion,
  isDescartada,
  tuvoDiferencia,
};
